import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Trash2, TriangleAlert, UserPlus } from 'lucide-react';
import { FilterSidebar } from '@/components/layout/FilterSidebar';
import { ListPageLayout } from '@/components/layout/ListPageLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAuth } from '@/hooks/useAuth';
import { getErrorMessage } from '@/services/apiClient';
import { useToast } from '@/hooks/useToast';
import { usePageContext } from '@/hooks/usePageContext';
import { useAsyncExport } from '@/hooks/useAsyncExport';
import type { FilterConfig, FilterOption, FilterValue } from '@/types/filters.types';
import {
  deleteUnmatchedLabResults,
  downloadLabResultFile,
  getLabResultDetail,
  listLabOrders,
  type LabOrderFilters,
  type LabDownloadResult,
  type LabResultDetail,
  type LabResultItem,
  type LabResultListItem,
  type LabResultObservation,
  type LabResultSection,
  type LabResultSource,
  type LabSearchType,
} from '@/services/lab-orders.service';
import { LabMatchDialog } from './LabMatchDialog';
import { LabResultOrderCell } from './LabResultOrderCell';
import { AccessLabDetailModal } from '@/pages/patients/labs/AccessLabDetailModal';
import {
  PATIENT_MATCH_OPTIONS,
  canDeleteLabResult,
  canDeleteLabResults,
  isUnmatchedResult,
  labResultRowClassName,
  patientMatchFromFilterValue,
  patientMatchToFilterValue,
  readPatientMatchParam,
  selectedDeleteItems,
  type PatientMatch,
} from './labOrdersFilters';
import { parseDateValue } from '@/lib/utils';

const FILTER_CONFIGS: FilterConfig[] = [
  { key: 'search', label: 'Search', type: 'search', allLabel: 'Search lab results...' },
  { key: 'searchType', label: 'Search Type', type: 'select', allLabel: 'Any field' },
  { key: 'patientMatch', label: 'Patient Match', type: 'select', allLabel: 'All results' },
  { key: 'daterange', label: 'Date Range', type: 'daterange' },
];

const SEARCH_TYPE_OPTIONS: Array<{ value: LabSearchType; label: string }> = [
  { value: 'any', label: 'Any' },
  { value: 'name', label: 'Name' },
  { value: 'patientId', label: 'Patient ID' },
  { value: 'tags', label: 'Tags' },
  { value: 'phone', label: 'Phone' },
  { value: 'birthDate', label: 'Birth Date' },
  { value: 'email', label: 'E-Mail' },
];

const PROVIDER_STYLES: Record<LabResultSource, string> = {
  quest: 'bg-blue-50 text-blue-700 border border-blue-200',
  vibrant: 'bg-cyan-50 text-cyan-700 border border-cyan-200',
  access: 'bg-teal-50 text-teal-700 border border-teal-200',
};

const PROVIDER_LABELS: Record<LabResultSource, string> = {
  quest: 'Quest',
  vibrant: 'Vibrant',
  access: 'Access Labs',
};

interface DetailModalState {
  source: LabResultSource;
  labResultId: number;
  patientName: string;
}

/**
 * Only ever used for a patient's birthDate. Parsed via `parseDateValue` so a
 * date-only 'YYYY-MM-DD' is read as LOCAL midnight — a bare `new Date(value)`
 * lands on UTC midnight and renders as the previous day in a negative-offset
 * zone. A DOB is a plain calendar date and must never be timezone-converted.
 */
function formatDate(value: string | null | undefined): string {
  const date = parseDateValue(value);
  if (!date) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function ProviderBadge({ source }: { source: LabResultSource }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${PROVIDER_STYLES[source]}`}>
      {PROVIDER_LABELS[source]}
    </span>
  );
}

function triggerBlobDownload({ blob, filename, contentType }: LabDownloadResult) {
  const objectUrl = URL.createObjectURL(blob);
  const resolvedName = filename || 'lab-document';
  const isPdf = (contentType || blob.type || '').toLowerCase().includes('pdf');

  if (isPdf) {
    // Open the PDF inline in a new tab only (legacy show_lab_file parity) and
    // never auto-download. We intentionally omit `noopener` here: with it,
    // window.open always returns null, which previously defeated the
    // popup-blocked check and caused BOTH a tab and a fallback download to
    // fire. The blob is our own content, so there's no opener attack surface.
    const opened = window.open(objectUrl, '_blank');
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    if (!opened) {
      throw new Error('Your browser blocked the document tab. Allow pop-ups for this site, then try again.');
    }
    return;
  }

  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = resolvedName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}

async function openExternal(url: string | null | undefined) {
  if (!url) return;

  if (!url.startsWith('/api/')) {
    window.open(url, '_blank', 'noopener,noreferrer');
    return;
  }

  const download = await downloadLabResultFile(url);
  if (download) triggerBlobDownload(download);
}

function parseNumericValue(value: string | null | undefined): number | null {
  if (!value) return null;
  const normalized = value.replace(/[<>=,\s]/g, '');
  if (!/^[-+]?\d*\.?\d+$/.test(normalized)) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseReferenceRange(range: string | null | undefined): { min: number; max: number } | null {
  if (!range) return null;
  const normalized = range.replace(/\s+/g, '');

  if (normalized.includes('-')) {
    const [min, max] = normalized.split('-', 2).map(Number);
    if (Number.isFinite(min) && Number.isFinite(max)) {
      return { min, max };
    }
  }

  if (normalized.includes('<')) {
    const limit = Number(normalized.replace(/[<=]/g, ''));
    if (Number.isFinite(limit)) {
      return { min: 0, max: limit };
    }
  }

  if (normalized.includes('>')) {
    const limit = Number(normalized.replace(/[>=]/g, ''));
    if (Number.isFinite(limit)) {
      return { min: limit, max: limit * 2 || limit + 1 };
    }
  }

  return null;
}

function flagDescription(flag: string | null | undefined): string | null {
  switch (flag) {
    case 'L':
      return 'Below low normal';
    case 'LL':
      return 'Below lower panic limits';
    case 'H':
      return 'Above high normal';
    case 'HH':
      return 'Above upper panic limits';
    case 'A':
      return 'Abnormal';
    case 'AA':
      return 'Very abnormal';
    case 'N':
      return 'Normal';
    default:
      return flag ? flag : null;
  }
}

function ReferenceRangeDetails({ observation }: { observation: LabResultObservation }) {
  const numericValue = parseNumericValue(observation.value);
  const range = parseReferenceRange(observation.referenceRange);

  if (numericValue === null || range === null) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        <div className="grid gap-2 sm:grid-cols-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Result</p>
            <p className="mt-1 font-medium text-slate-900">{observation.value || '—'}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reference Range</p>
            <p className="mt-1 font-medium text-slate-900">{observation.referenceRange || '—'}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Interpretation</p>
            <p className={`mt-1 font-medium ${observation.abnormal ? 'text-red-700' : 'text-slate-900'}`}>
              {flagDescription(observation.flag) || 'No additional interpretation'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const span = Math.max(range.max - range.min, 0.0001);
  const paddedMin = Math.min(range.min, numericValue);
  const paddedMax = Math.max(range.max, numericValue);
  const totalSpan = Math.max(paddedMax - paddedMin, span);
  const leftPercent = ((range.min - paddedMin) / totalSpan) * 100;
  const widthPercent = ((range.max - range.min) / totalSpan) * 100;
  const valuePercent = ((numericValue - paddedMin) / totalSpan) * 100;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Result</p>
          <p className={`mt-1 text-sm font-semibold ${observation.abnormal ? 'text-red-700' : 'text-slate-900'}`}>
            {observation.value}
            {observation.unit ? <span className="ml-1 text-xs font-normal text-slate-500">{observation.unit}</span> : null}
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reference Range</p>
          <p className="mt-1 text-sm font-medium text-slate-900">{observation.referenceRange || '—'}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Interpretation</p>
          <p className={`mt-1 text-sm font-medium ${observation.abnormal ? 'text-red-700' : 'text-slate-900'}`}>
            {flagDescription(observation.flag) || 'Within expected range'}
          </p>
        </div>
      </div>

      <div className="mt-4">
        <div className="relative h-3 rounded-full bg-red-200">
          <div
            className="absolute top-0 h-3 rounded-full bg-blue-200"
            style={{ left: `${leftPercent}%`, width: `${widthPercent}%` }}
          />
          <div
            className={`absolute top-1/2 h-5 w-1 -translate-y-1/2 rounded-full ${observation.abnormal ? 'bg-red-700' : 'bg-slate-900'}`}
            style={{ left: `calc(${valuePercent}% - 2px)` }}
          />
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
          <span>{range.min}</span>
          <span>{range.max}</span>
        </div>
      </div>
    </div>
  );
}

function ObservationRow({ observation }: { observation: LabResultObservation }) {
  const [expanded, setExpanded] = useState(false);
  const expandable = Boolean(observation.referenceRange || observation.flag);

  return (
    <>
      <tr
        className={`border-t border-slate-100 ${expandable ? 'cursor-pointer hover:bg-slate-50' : ''}`}
        onClick={expandable ? () => setExpanded((current) => !current) : undefined}
      >
        <td className="px-3 py-2 text-slate-700">{observation.label || observation.code || 'Result'}</td>
        <td className="px-3 py-2 font-mono text-xs text-slate-500">{observation.code || '—'}</td>
        <td className={`px-3 py-2 font-medium ${observation.abnormal ? 'text-red-700' : 'text-slate-900'}`}>
          {observation.value || '—'}
        </td>
        <td className="px-3 py-2 text-slate-600">{observation.referenceRange || '—'}</td>
        <td className="px-3 py-2 text-slate-600">{observation.unit || '—'}</td>
        <td className="px-3 py-2 text-slate-600">{flagDescription(observation.flag) || '—'}</td>
      </tr>
      {expandable && expanded && (
        <tr className="border-t border-slate-100 bg-white">
          <td colSpan={6} className="px-3 py-3">
            <ReferenceRangeDetails observation={observation} />
          </td>
        </tr>
      )}
    </>
  );
}

function renderOrderItems(items: LabResultItem[]) {
  if (items.length === 0) return null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="border-b border-slate-200 px-4 py-3">
        <h3 className="text-sm font-semibold text-slate-900">Order Items</h3>
      </div>
      <div className="divide-y divide-slate-100">
        {items.map((item) => (
          <div key={item.labOrderItemId} className="px-4 py-3">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-slate-900">{item.testName}</p>
                  {item.abnormal && (
                    <Badge variant="error">Abnormal</Badge>
                  )}
                </div>
                <p className="text-xs font-mono text-slate-500">{item.testCode || '—'}</p>
              </div>
              <div className="text-right">
                <p className={`text-sm font-semibold ${item.abnormal ? 'text-red-700' : 'text-slate-900'}`}>
                  {item.resultValue || 'Pending'}
                </p>
                <p className="text-xs text-slate-500">
                  {item.resultUnit || item.referenceRange ? `${item.resultUnit || ''}${item.referenceRange ? ` • Ref ${item.referenceRange}` : ''}` : '—'}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function renderSections(sections: LabResultSection[]) {
  if (sections.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
        No parsed result sections were available for this lab.
      </div>
    );
  }

  return sections.map((section) => (
    <div key={section.id} className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="border-b border-slate-200 px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{section.title}</h3>
            {section.subtitle && (
              <p className="text-xs font-mono text-slate-500">{section.subtitle}</p>
            )}
          </div>
          <div className="text-right text-xs text-slate-500">
            {section.collectedAt && <p>Collected: {formatDateTime(section.collectedAt)}</p>}
            {section.reportedAt && <p>Reported: {formatDateTime(section.reportedAt)}</p>}
          </div>
        </div>
      </div>
      {section.observations.length === 0 ? (
        <div className="px-4 py-4 text-sm text-slate-500">No observations in this section.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Test</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Code</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Result</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Ref. Range</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Unit</th>
                <th className="px-3 py-2 text-left font-semibold text-slate-600">Flag</th>
              </tr>
            </thead>
            <tbody>
              {section.observations.map((observation) => (
                <ObservationRow
                  key={`${section.id}-${observation.code}-${observation.label}`}
                  observation={observation}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  ));
}

function LabResultDetailModal({
  modal,
  onClose,
}: {
  modal: DetailModalState;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [downloadingUrl, setDownloadingUrl] = useState<string | null>(null);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const detailQuery = useQuery({
    queryKey: ['lab-result-detail', modal.source, modal.labResultId],
    queryFn: () => getLabResultDetail(modal.source, modal.labResultId),
  });

  const detail: LabResultDetail | undefined = detailQuery.data;
  const files = detail?.files ?? [];
  const sections = detail?.sections ?? [];
  const items = detail?.items ?? [];
  const isUnassociated = detail != null && detail.patientId === 0;
  const canDelete = detail != null && canDeleteLabResult(detail, user?.userLevel);

  const handleDelete = useCallback(async () => {
    try {
      await deleteUnmatchedLabResults([{ source: modal.source, id: modal.labResultId }]);
      toast.success('Lab result deleted', 'It has been removed from Lab Results.');
      void queryClient.invalidateQueries({ queryKey: ['lab-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['navigation', 'badges'] });
      onClose();
    } catch (error) {
      toast.error(
        'Lab result not deleted',
        getErrorMessage(error, 'This lab result could not be deleted. Refresh the list and try again.'),
      );
    }
  }, [modal.labResultId, modal.source, onClose, queryClient, toast]);

  const handleOpenFile = useCallback(async (url: string | null | undefined) => {
    if (!url) return;

    setDownloadingUrl(url);
    try {
      await openExternal(url);
    } catch (error) {
      toast.error(
        'Failed to open lab document',
        error instanceof Error ? error.message : 'The selected lab file could not be opened.',
      );
    } finally {
      setDownloadingUrl((current) => (current === url ? null : current));
    }
  }, [toast]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-lg font-semibold text-slate-900">
                {`${PROVIDER_LABELS[modal.source]} Lab Result`}
              </h2>
              <ProviderBadge source={modal.source} />
            </div>
            <p className="mt-1 text-sm text-slate-500">{modal.patientName}</p>
          </div>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {detailQuery.isLoading ? (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
              Loading lab details...
            </div>
          ) : detailQuery.isError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700">
              {detailQuery.error instanceof Error ? detailQuery.error.message : 'Failed to load lab details.'}
            </div>
          ) : detail ? (
            <div className="space-y-5">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Order</p>
                  <p className="mt-1 text-sm font-medium text-slate-900">{detail.orderNumber ?? '—'}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Patient ID</p>
                  <p className="mt-1 text-sm font-medium text-slate-900">{detail.patientDisplayId ? `#${detail.patientDisplayId}` : '—'}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Birth Date</p>
                  <p className="mt-1 text-sm font-medium text-slate-900">{formatDate(detail.birthDate)}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Received</p>
                  <p className="mt-1 text-sm font-medium text-slate-900">{formatDateTime(detail.orderedAt)}</p>
                </div>
              </div>

              {isUnassociated && (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                  <div className="flex items-start gap-2">
                    <TriangleAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">This lab result is not linked to a patient.</p>
                      <p className="text-xs text-amber-700">Match it to a patient to file it in their chart.</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {canDelete && (
                      <Button variant="secondary" onClick={() => setDeleteDialogOpen(true)}>
                        <Trash2 className="mr-1.5 h-4 w-4" />
                        Delete
                      </Button>
                    )}
                    <Button variant="secondary" onClick={() => setMatchDialogOpen(true)}>
                      <UserPlus className="mr-1.5 h-4 w-4" />
                      Match to Patient
                    </Button>
                  </div>
                </div>
              )}

              <div className="grid gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
                <div className="space-y-5">
                  <div className="rounded-lg border border-slate-200 bg-white px-4 py-4">
                    <div className="flex flex-wrap items-center gap-2">
                      {detail.source === 'quest' && !detail.orderFound && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 border border-amber-200">
                          <TriangleAlert className="h-3.5 w-3.5" />
                          Missing linked Quest order
                        </span>
                      )}
                      {detail.status && (
                        <Badge variant="neutral">{detail.status}</Badge>
                      )}
                    </div>
                    <dl className="mt-4 grid gap-3 sm:grid-cols-2">
                      <div>
                        <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Test Summary</dt>
                        <dd className="mt-1 text-sm text-slate-900">{detail.testSummary ?? '—'}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Control ID</dt>
                        <dd className="mt-1 text-sm text-slate-900">{detail.summary.controlId ?? '—'}</dd>
                      </div>
                      {detail.summary.requestId && (
                        <div>
                          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Request ID</dt>
                          <dd className="mt-1 text-sm text-slate-900">{detail.summary.requestId}</dd>
                        </div>
                      )}
                      {detail.summary.providerName && (
                        <div>
                          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Provider</dt>
                          <dd className="mt-1 text-sm text-slate-900">
                            {detail.summary.providerName}
                            {detail.summary.providerNpi ? ` (${detail.summary.providerNpi})` : ''}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  {renderOrderItems(items)}

                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Result Sections</h3>
                    {renderSections(sections)}
                  </div>
                </div>

                <div className="space-y-5">
                  <div className="rounded-lg border border-slate-200 bg-white px-4 py-4">
                    <h3 className="text-sm font-semibold text-slate-900">Documents</h3>
                    <div className="mt-3 space-y-2">
                      {files.length === 0 ? (
                        <p className="text-sm text-slate-500">No documents available.</p>
                      ) : (
                        files.map((file) => (
                          <button
                            key={`${file.type}-${file.name}`}
                            type="button"
                            onClick={() => { void handleOpenFile(file.url); }}
                            disabled={!file.url || downloadingUrl === file.url}
                            className="flex w-full items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <span className="min-w-0">
                              <span className="block truncate font-medium">{file.label}</span>
                              <span className="block truncate text-xs text-slate-500">{file.name || file.type}</span>
                            </span>
                            <span className="ml-3 flex-shrink-0 text-xs text-slate-500">{formatDateTime(file.receivedAt)}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {matchDialogOpen && detail && (
        <LabMatchDialog
          isOpen={matchDialogOpen}
          onClose={() => setMatchDialogOpen(false)}
          source={modal.source}
          labResultId={modal.labResultId}
          resultPatientName={detail.patientName}
          onAssociated={() => { void detailQuery.refetch(); }}
        />
      )}

      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete this lab result?"
        message={modal.source === 'quest'
          ? 'It will be removed from Lab Results, along with any other results on the same order. Use this only for results that do not belong to any patient.'
          : 'It will be removed from Lab Results. Use this only for results that do not belong to any patient.'}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}

export default function LabOrdersPage() {
  const updateAIContext = usePageContext('Lab Results');
  const [search, setSearch] = useState('');
  const [searchType, setSearchType] = useState<LabSearchType>('any');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Seeded once from the URL so the Labs nav bubble can link straight to the
  // unmatched results; thereafter the sidebar owns it and the URL is not
  // rewritten (none of the other filters on this page are URL-backed either).
  const [searchParams] = useSearchParams();
  const [patientMatch, setPatientMatch] = useState<PatientMatch>(() => readPatientMatchParam(searchParams));
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<LabOrderFilters['sortBy']>('orderedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedResult, setSelectedResult] = useState<DetailModalState | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<Set<string | number>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const perPage = 25;
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canDelete = canDeleteLabResults(user?.userLevel);

  useEffect(() => {
    setPage(1);
  }, [search, searchType, dateFrom, dateTo, patientMatch]);

  // A bulk delete must only ever act on rows the user can see.
  useEffect(() => {
    setSelectedKeys(new Set());
  }, [search, searchType, dateFrom, dateTo, patientMatch, page, sortBy, sortDir]);

  const filters: LabOrderFilters = {
    search: search.trim() || undefined,
    searchType,
    patientMatch: patientMatch ?? undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    page,
    perPage,
    sortBy,
    sortDir,
  };

  const { exporting, startExport, exportModal } = useAsyncExport({ entityType: 'lab-orders', entityLabel: 'lab results' });

  const labsQuery = useQuery({
    queryKey: ['lab-orders', filters],
    queryFn: () => listLabOrders(filters),
    placeholderData: (previous) => previous,
  });

  const rows = labsQuery.data?.data ?? [];
  const meta = labsQuery.data?.meta;
  const bulkDeleteItems = selectedDeleteItems(selectedKeys, rows);

  const handleBulkDelete = useCallback(async () => {
    const items = selectedDeleteItems(selectedKeys, rows);
    if (items.length === 0) return;
    try {
      await deleteUnmatchedLabResults(items);
      toast.success(
        items.length === 1 ? 'Lab result deleted' : 'Lab results deleted',
        items.length === 1
          ? 'It has been removed from Lab Results.'
          : `${items.length} lab results have been removed from Lab Results.`,
      );
      setSelectedKeys(new Set());
      void queryClient.invalidateQueries({ queryKey: ['lab-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['navigation', 'badges'] });
    } catch (error) {
      toast.error(
        'Lab results not deleted',
        getErrorMessage(error, 'The selected lab results could not be deleted. Refresh the list and try again.'),
      );
    }
  }, [queryClient, rows, selectedKeys, toast]);

  const sidebarOptions = useMemo<Record<string, FilterOption[]>>(
    () => ({
      searchType: SEARCH_TYPE_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
      })),
      patientMatch: PATIENT_MATCH_OPTIONS,
    }),
    [],
  );

  const sidebarValues = useMemo<Record<string, FilterValue>>(
    () => ({
      search: search ? [search] : [],
      searchType: searchType === 'any' ? [] : [searchType],
      patientMatch: patientMatchToFilterValue(patientMatch),
      daterange: { from: dateFrom, to: dateTo },
    }),
    [search, searchType, patientMatch, dateFrom, dateTo],
  );

  const handleSidebarChange = useCallback((filterKey: string, value: FilterValue) => {
    switch (filterKey) {
      case 'search':
        if (Array.isArray(value)) {
          setSearch(value[0] ?? '');
        }
        break;
      case 'searchType':
        if (Array.isArray(value)) {
          setSearchType((value[0] as LabSearchType | undefined) ?? 'any');
        }
        break;
      case 'patientMatch':
        setPatientMatch(patientMatchFromFilterValue(value));
        break;
      case 'daterange':
        if (!Array.isArray(value) && 'from' in value) {
          setDateFrom(value.from || '');
          setDateTo(value.to || '');
        }
        break;
    }
  }, []);

  const handleSidebarClear = useCallback((filterKey: string) => {
    switch (filterKey) {
      case 'search':
        setSearch('');
        break;
      case 'searchType':
        setSearchType('any');
        break;
      case 'patientMatch':
        setPatientMatch(null);
        break;
      case 'daterange':
        setDateFrom('');
        setDateTo('');
        break;
    }
  }, []);

  function handleExportCsv() {
    startExport({ ...filters });
  }

  useEffect(() => {
    const filterParts: string[] = [];
    if (search.trim()) filterParts.push(`Search: "${search.trim()}"`);
    if (searchType !== 'any') {
      const option = SEARCH_TYPE_OPTIONS.find((item) => item.value === searchType);
      filterParts.push(`Search type: ${option?.label ?? searchType}`);
    }
    if (patientMatch) {
      filterParts.push(patientMatch === 'unmatched' ? 'Not matched to a patient' : 'Matched to a patient');
    }
    if (dateFrom || dateTo) filterParts.push(`Date: ${dateFrom || '...'} to ${dateTo || '...'}`);

    updateAIContext({
      filters: filterParts.length > 0 ? filterParts.join(', ') : 'No filters applied',
      dataSummary: `${meta?.total ?? 0} lab results on page ${meta?.page ?? page}`,
    });
  }, [search, searchType, patientMatch, dateFrom, dateTo, meta?.total, meta?.page, page, updateAIContext]);

  const columns = useMemo<DataTableColumn<LabResultListItem>[]>(() => [
    {
      key: 'orderedAt',
      header: 'Date',
      sortable: true,
      render: (row) => <span className="text-slate-700">{formatDateTime(row.orderedAt)}</span>,
    },
    {
      key: 'orderNumber',
      header: 'Order ID',
      sortable: true,
      render: (row) => <LabResultOrderCell row={row} />,
    },
    {
      key: 'patientDisplayId',
      header: 'Patient ID',
      sortable: true,
      render: (row) => (
        isUnmatchedResult(row)
          // Colour alone is not a signal — say what is wrong in the cell that
          // is wrong. Keyed on patientId, never on a missing patientDisplayId:
          // a matched patient can have a blank display id too.
          ? <span className="text-xs font-medium text-red-700">Not matched</span>
          : <span className="font-mono text-xs text-slate-600">{row.patientDisplayId ? `#${row.patientDisplayId}` : '—'}</span>
      ),
    },
    {
      key: 'patientName',
      header: 'Name',
      sortable: true,
      render: (row) => <span className="font-medium text-slate-900">{row.patientName}</span>,
    },
    {
      key: 'birthDate',
      header: 'Birth Date',
      sortable: true,
      render: (row) => <span className="text-slate-700">{formatDate(row.birthDate)}</span>,
    },
    {
      key: 'testsSummary',
      header: 'Test(s)',
      sortable: true,
      render: (row) => <span className="block max-w-[28rem] truncate text-slate-700">{row.testsSummary ?? '—'}</span>,
    },
  ], []);

  return (
    <>
      <ListPageLayout
        filterSidebar={(
          <FilterSidebar
            filters={FILTER_CONFIGS}
            values={sidebarValues}
            options={sidebarOptions}
            onChange={handleSidebarChange}
            onClear={handleSidebarClear}
            showAIAssistant={false}
          />
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="shrink-0 space-y-4 p-6">
            <PageHeader
              variant="compact"
              title="Lab Results"
              actions={(
                <div className="flex items-center gap-2">
                  {canDelete && bulkDeleteItems.length > 0 && (
                    <Button variant="secondary" onClick={() => setBulkDeleteOpen(true)}>
                      <Trash2 className="mr-1.5 h-4 w-4" />
                      Delete selected ({bulkDeleteItems.length})
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    onClick={handleExportCsv}
                    disabled={rows.length === 0 || exporting}
                    loading={exporting}
                  >
                    Export
                  </Button>
                  {exportModal}
                </div>
              )}
            />

          </div>

          <div className="min-h-0 flex-1 overflow-auto">
            <DataTable<LabResultListItem>
              data={rows}
              columns={columns}
              rowClassName={labResultRowClassName}
              keyField="rowKey"
              selectable={canDelete}
              isRowSelectable={isUnmatchedResult}
              selectedIds={selectedKeys}
              onSelectionChange={setSelectedKeys}
              loading={labsQuery.isLoading}
              onRowClick={(row) => setSelectedResult({
                source: row.source,
                labResultId: row.labResultId,
                patientName: row.patientName,
              })}
              page={meta?.page ?? page}
              pageSize={meta?.perPage ?? perPage}
              total={meta?.total ?? 0}
              onPageChange={setPage}
              emptyTitle="No lab results found"
              emptyDescription="Try adjusting your search or date range."
              manualSort
              sortKey={sortBy ?? null}
              sortDir={sortDir}
              onSortChange={(key, direction) => {
                setSortBy(key as LabOrderFilters['sortBy']);
                setSortDir(direction);
              }}
            />
          </div>
        </div>
      </ListPageLayout>

      {/* Access results open their own view: versions, flags in words, print. */}
      {selectedResult && selectedResult.source === 'access' && (
        <AccessLabDetailModal
          open
          onClose={() => setSelectedResult(null)}
          labResultId={selectedResult.labResultId}
        />
      )}
      {selectedResult && selectedResult.source !== 'access' && (
        <LabResultDetailModal
          modal={selectedResult}
          onClose={() => setSelectedResult(null)}
        />
      )}

      <ConfirmDialog
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={handleBulkDelete}
        title={bulkDeleteItems.length === 1 ? 'Delete 1 lab result?' : `Delete ${bulkDeleteItems.length} lab results?`}
        message="They will be removed from Lab Results. A Quest result also removes the other results on the same order. Use this only for results that do not belong to any patient."
        confirmLabel="Delete"
        variant="danger"
      />
    </>
  );
}
